import type { Href } from "expo-router";
import { COLORS } from "../lib/brand";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { Button, Card, Section } from "../components/ui";
import { supabase } from "../lib/supabase";
import {
  acceptInvite,
  fetchInviteDisplayNames,
  getInviteByToken,
  isEmailMismatchAcceptError,
  portalHrefAfterAccept,
  type InviteByToken,
  type InviteKind,
} from "../lib/invites";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

function signupHref(kind: InviteKind, email: string, token: string): Href {
  const enc = encodeURIComponent;
  const pair = `email=${enc(email)}&token=${enc(token)}`;
  if (kind === "client") return `/signup?role=client&${pair}` as Href;
  if (kind === "judge") return `/signup/judge?${pair}` as Href;
  if (kind === "employee") return `/signup/employee?${pair}` as Href;
  return `/signup?role=student&${pair}` as Href;
}

function loginHref(email: string, token: string): Href {
  return `/login?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}` as Href;
}

export default function AcceptInviteScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const rawToken = firstParam(params.token);

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteByToken | null>(null);
  const [invalid, setInvalid] = useState<"missing" | "gone" | null>(null);
  const [headline, setHeadline] = useState("");
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!rawToken?.trim()) {
      setInvalid("missing");
      setLoading(false);
      return;
    }
    setLoading(true);
    setInvalid(null);
    setErr(null);
    const row = await getInviteByToken(rawToken.trim());
    if (!row) {
      setInvalid("gone");
      setInvite(null);
      setLoading(false);
      return;
    }
    if (row.status !== "pending") {
      setInvalid("gone");
      setInvite(null);
      setLoading(false);
      return;
    }
    const exp = new Date(row.expires_at).getTime();
    if (Number.isFinite(exp) && exp < Date.now()) {
      setInvalid("gone");
      setInvite(null);
      setLoading(false);
      return;
    }
    setInvite(row);
    const { companyName, competitionName } = await fetchInviteDisplayNames(row);
    if (row.kind === "client") {
      const org = companyName ?? "a company on Coravu";
      setHeadline(`You've been invited to join ${org} on Coravu.`);
    } else if (row.kind === "judge") {
      const comp = competitionName ?? "a competition";
      setHeadline(`You've been invited to judge ${comp}.`);
    } else if (row.kind === "employee") {
      const r = row.role ?? "an admin";
      setHeadline(`You've been invited to join Coravu as ${r}.`);
    } else {
      setHeadline("You've been invited to Coravu.");
    }
    const { data } = await supabase.auth.getUser();
    setAuthEmail(data.user?.email?.toLowerCase() ?? null);
    setLoading(false);
  }, [rawToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onContinueAsSignedIn() {
    if (!rawToken?.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await acceptInvite(rawToken.trim());
      router.replace(portalHrefAfterAccept(result));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not accept invite";
      if (isEmailMismatchAcceptError(msg)) {
        setErr(
          "This invite was sent to a different email. Sign out and use that email, or contact the person who invited you."
        );
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center bg-linen">
        <ActivityIndicator size="large" color={COLORS.FOREST.hex} />
      </SafeAreaView>
    );
  }

  if (invalid === "missing") {
    return (
      <SafeAreaView className="flex-1 bg-bg px-5 pt-8">
        <Text className="text-xl font-semibold text-primary">Invalid link</Text>
        <Text className="mt-2 text-sm text-primary/80">This invite URL is missing a token.</Text>
        <Link href="/" asChild>
          <Pressable className="mt-6 self-start rounded-md bg-primary px-4 py-2">
            <Text className="font-semibold text-white">Go home</Text>
          </Pressable>
        </Link>
      </SafeAreaView>
    );
  }

  if (invalid === "gone" || !invite) {
    return (
      <SafeAreaView className="flex-1 bg-bg px-5 pt-8">
        <Text className="text-xl font-semibold text-primary">This invite link is no longer valid.</Text>
        <Text className="mt-2 text-sm leading-relaxed text-primary/80">
          It may have expired, been revoked, or already used. Ask your host for a new invite if you still need access.
        </Text>
        <Link href="/" asChild>
          <Pressable className="mt-6 self-start rounded-md border border-border bg-white px-4 py-2">
            <Text className="font-semibold text-primary">Go home</Text>
          </Pressable>
        </Link>
      </SafeAreaView>
    );
  }

  const email = invite.email;
  const signedIn = !!authEmail;
  const emailMatches = signedIn && authEmail === email.toLowerCase();

  return (
    <SafeAreaView className="flex-1 bg-linen" edges={["top", "left", "right"]}>
      <ScrollView className="flex-1 px-5 pt-6" contentContainerStyle={{ paddingBottom: 32 }}>
        <Card variant="light" padding="lg">
          <Section overline="Invitation" title="You're invited" subtitle={headline}>
          <Text className="mt-2 text-sm text-primary/60">
            Invited email: <Text className="font-medium text-primary">{email}</Text>
          </Text>

        {err ? <Text className="mt-4 text-sm text-red-600">{err}</Text> : null}

        {signedIn ? (
          <View className="mt-8 gap-3">
            {!emailMatches ? (
              <Text className="text-sm text-amber-800">
                {"You're signed in as "}
                {authEmail}. This invite is for {email}. Sign out to continue with the invited email, or use a different
                browser/profile.
              </Text>
            ) : null}
            <Button variant="primary" loading={busy} disabled={!emailMatches} onPress={() => void onContinueAsSignedIn()}>
              Continue as {email}
            </Button>
          </View>
        ) : (
          <View className="mt-8 gap-3">
            <Link href={loginHref(email, rawToken ?? "")} asChild>
              <View><Button variant="primary">Sign in</Button></View>
            </Link>
            <Link href={signupHref(invite.kind, email, rawToken ?? "")} asChild>
              <View><Button variant="ghost-light">Create account</Button></View>
            </Link>
            {invite.kind === "employee" ? (
              <Text className="text-center text-sm text-primary/70">Use the invited work email. Already registered? Sign in above.</Text>
            ) : null}
          </View>
        )}
          </Section>
        </Card>
      </ScrollView>
    </SafeAreaView>
  );
}
